import { useEffect } from "react";

/**
 * Simple SEO hook: sets document title, meta description, keywords, canonical, and Open Graph tags.
 * Cleans up on unmount (restores previous values).
 */
export function useSEO({ title, description, keywords, image, canonical, jsonLd }) {
  useEffect(() => {
    const prev = {
      title: document.title,
      desc: getMeta("name", "description"),
      kw: getMeta("name", "keywords"),
      ogT: getMeta("property", "og:title"),
      ogD: getMeta("property", "og:description"),
      ogI: getMeta("property", "og:image"),
      ogU: getMeta("property", "og:url"),
      twT: getMeta("name", "twitter:title"),
      twD: getMeta("name", "twitter:description"),
      canonical: (document.querySelector('link[rel="canonical"]') || {}).href,
    };

    if (title) {
      document.title = title;
      setMeta("property", "og:title", title);
      setMeta("name", "twitter:title", title);
    }
    if (description) {
      setMeta("name", "description", description);
      setMeta("property", "og:description", description);
      setMeta("name", "twitter:description", description);
    }
    if (keywords) setMeta("name", "keywords", keywords);
    if (image) {
      setMeta("property", "og:image", image);
      setMeta("name", "twitter:image", image);
    }
    const canonicalUrl = canonical || window.location.href;
    setLink("canonical", canonicalUrl);
    setMeta("property", "og:url", canonicalUrl);

    let jsonLdEl = null;
    if (jsonLd) {
      const list = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      jsonLdEl = list.map((obj) => {
        const el = document.createElement("script");
        el.type = "application/ld+json";
        el.dataset.dynamic = "true";
        el.text = JSON.stringify(obj);
        document.head.appendChild(el);
        return el;
      });
    }

    return () => {
      if (title) {
        document.title = prev.title;
        if (prev.ogT != null) setMeta("property", "og:title", prev.ogT);
        if (prev.twT != null) setMeta("name", "twitter:title", prev.twT);
      }
      if (description) {
        if (prev.desc != null) setMeta("name", "description", prev.desc);
        if (prev.ogD != null) setMeta("property", "og:description", prev.ogD);
        if (prev.twD != null) setMeta("name", "twitter:description", prev.twD);
      }
      if (keywords && prev.kw != null) setMeta("name", "keywords", prev.kw);
      if (image) {
        if (prev.ogI != null) setMeta("property", "og:image", prev.ogI);
      }
      if (prev.canonical) setLink("canonical", prev.canonical);
      if (Array.isArray(jsonLdEl)) {
        jsonLdEl.forEach((el) => { if (el.parentNode) el.parentNode.removeChild(el); });
      }
    };
  }, [title, description, keywords, image, canonical, JSON.stringify(jsonLd)]);
}

function getMeta(attr, val) {
  const el = document.querySelector(`meta[${attr}="${val}"]`);
  return el ? el.getAttribute("content") : null;
}
function setMeta(attr, val, content) {
  let el = document.querySelector(`meta[${attr}="${val}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, val);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}
function setLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}
